<template>
  <b-card class="chat-widget">
    <b-card-title class="mb-75">
      Comentarios
      <b-spinner
        small
        class="float-right"
        variant="primary"
        v-if="isLoading"
      />
    </b-card-title>
    <div class="activity-comments pt-4">
      <a
        v-if="hasMorePages"
        @click="fetchMore"
        class="btn-block btn btn-link"
      >
        Cargar mas comentarios
      </a>
      <div
        v-for="comment in commentsSorted"
        :key="comment.id"
        class="d-flex align-items-start mb-4"
      >
        <b-avatar
          v-if="!comment.userAvatar"
          :text="comment.username | avatarName"
          size="34"
          class="mt-25 mr-75"
        />
        <b-avatar
          v-else
          :src="comment.userAvatar"
          size="34"
          class="mt-25 mr-75"
        />
        <div class="profile-user-info w-100">
          <div :title="comment.createdAt | dateFormatTime">
            <small>{{ comment.username }}</small>
            -
            <small>{{ comment.createdAt | fromNow }}</small>
          </div>
          <div class="d-flex align-items-center justify-content-between">
            <h6 class="mb-0">
              {{comment.text }}
            </h6>
          </div>
          <div class="my-1" v-if="comment.attachments.length">
            <DownloadFile
              v-for="attachment in comment.attachments"
              :key="`${comment.id}-attachment-${attachment.id}`"
              :path="attachment.url"
              :name="attachment.name"
              class="btn btn-outline-secondary btn-sm mr-1 mb-1"
            />
          </div>
        </div>
      </div>
    </div>
    <b-form @submit.prevent="onSubmit()" data-vv-scope="comment-form">
      <b-form-group>
        <b-form-textarea
          rows="3"
          placeholder="Agregar comentario"
          name="comment"
          v-model="comment.text"
          :state="errors.has('comment-form.comment') ? false : null"
          v-validate="'required'"
        />
        <b-form-invalid-feedback>
          {{ errors.first('comment-form.comment') }}
        </b-form-invalid-feedback>
      </b-form-group>
      <div class="mb-1">
        <b-button
          v-for="(attachment, index) in attachments"
          :key="`attachment-${attachment.id}`"
          variant="outline-secondary form-attachment"
          @click="deleteCommentAttachment(index)"
          size="sm"
          class="mr-1 mb-1"
        >
          {{attachment.name}} <feather-icon icon="PaperclipIcon" />
        </b-button>
      </div>
      <div class="d-flex" v-has-permission="'ED080'">
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          size="sm"
          variant="primary"
          type="submit"
          class="mr-1"
          :disabled="isLoading"
        >
          Comentar
        </b-button>
        <UploadFile
          permission="*"
          @input="addCommentAttachment"
          :upload-to="attachmentUrl"
          type-button="small"
          button-class=""
        />
        <div class="col-md-12">
          <div v-if="loadFileFailed" class="con-text-validation span-text-validation-danger
           vs-input--text-validation-span v-enter-to"
               style="height: 32px;">
          <span class="span-text-validation">
            La carga del archivo falló, intentalo de nuevo
          </span>
          </div>
        </div>
      </div>
    </b-form>
  </b-card>
</template>
<script>

import { clActivityCommentList, clActivityCommentCreate } from '@/febos/servicios/api/activities.api';
import UploadFile from '@/febos/global/_vue/componentes/UploadFile';
import DownloadFile from '@/febos/chile/dnt/components/DownloadFile';

export default {
  data() {
    return {
      loadFileFailed: false,
      currentPage: 1,
      totalPages: 1,
      comments: {},
      comment: {},
      attachments: [],
      isLoading: false
    };
  },
  computed: {
    commentsSorted() {
      return Object.values(this.comments)
        .sort((a, b) => this.$moment(a.createdAt).diff(this.$moment(b.createdAt)));
    },
    hasMorePages() {
      return this.currentPage < this.totalPages;
    },
    attachmentUrl() {
      return `febos-io/global/${process.env.VUE_APP_AMBIENTE}/actividades/${this.activityId}/archivos`;
    }
  },
  methods: {
    async fetch() {
      this.isLoading = true;
      const { comments, totalPages, currentPage } = await clActivityCommentList(this.activityId);
      this.comments = comments.reduce((memo, comment) => ({
        ...memo,
        [comment.id]: comment
      }), {});
      this.totalPages = totalPages;
      this.currentPage = currentPage;
      this.isLoading = false;
    },
    async fetchMore() {
      this.isLoading = true;
      const { comments, totalPages, currentPage } = await clActivityCommentList(
        this.activityId, this.currentPage + 1
      );
      this.comments = comments.reduce((memo, comment) => ({
        ...memo,
        [comment.id]: comment
      }), this.comments);
      this.totalPages = totalPages;
      this.currentPage = currentPage;
      this.isLoading = false;
    },
    addCommentAttachment(
      {
        failed,
        message,
        id,
        path: url,
        name
      }
    ) {
      this.loadFileFailed = false;
      if (failed) {
        this.loadFileFailed = true;
        this.$bvToast.toast(
          message,
          {
            title: 'Tablero',
            variant: 'danger',
            pauseOnHover: true,
            solid: true,
            noAutoHide: true
          }
        );
        return;
      }
      this.attachments.push({ id, url, name });
    },
    deleteCommentAttachment(index) {
      this.attachments = [
        ...this.attachments.slice(0, index),
        ...this.attachments.slice(index + 1)
      ];
    },
    async onSubmit() {
      const result = await this.$validator.validateAll('comment-form');
      if (!result) {
        return;
      }
      this.isLoading = true;
      const comment = await clActivityCommentCreate(
        this.activityId,
        {
          ...this.comment,
          attachments: this.attachments
        }
      );
      this.comments = {
        ...this.comments,
        [comment.id]: comment
      };
      this.$bvToast.toast(
        'Comentario agregado',
        {
          title: 'Tablero',
          variant: 'primary',
          pauseOnHover: true,
          solid: true,
          noAutoHide: true
        }
      );
      await this.onReset();
    },
    async onReset() {
      this.comment = {};
      this.attachments = [];
      await this.$validator.reset();
      this.isLoading = false;
    }
  },
  created() {
    this.fetch();
  },
  components: { UploadFile, DownloadFile },
  props: {
    activityId: { type: [Number, String], required: true }
  }
};

</script>
<style lang="scss" scoped>

@import '~@core/scss/base/core/colors/palette-variables.scss';

.form-attachment:hover {
  border-color: $danger !important;
  color: $danger !important;
}

</style>
